import { useState } from "react";
import { CustomDropdown } from "@/components/ui/dropdown/custom-dropdown";
import { useCaseMapping } from "@/utils/use-case-mapping";
import { Button } from "../../../components/ui/button";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Checkbox } from "../../../components/ui/form";

/* Prop Types */
interface UseCaseDropdownProps {
  useCases: { id: string; name: string }[];
  onSelectionChange: (selectedIds: string[]) => void;
}

/*
 * UseCaseDropdown component
 *
 * This component renders a dropdown for filtering contexts based on use case.
 * It categorizes use cases based on the useCaseMapping utility as it's not returned from the backend.
 */
export const UseCaseDropdown = ({ useCases, onSelectionChange }: UseCaseDropdownProps) => {
  /* State */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  /* Handlers */
  const handleCheckboxChange = (id: string) => {
    if (id === "all") {
      const newSelectedIds = selectedIds.includes("all") || selectedIds.length === useCases.length ? [] : useCases.map((useCase) => useCase.id);
      setSelectedIds(newSelectedIds);
      onSelectionChange(newSelectedIds);
    } else {
      const newSelectedIds = selectedIds.includes(id) ? selectedIds.filter((selectedId) => selectedId !== id) : [...selectedIds.filter((selectedId) => selectedId !== "all"), id];
      setSelectedIds(newSelectedIds);
      onSelectionChange(newSelectedIds);
    }
  };

  /* Reducers */
  const categorizedUseCases = useCases.reduce(
    // we group the use cases by category - this is to save us having to call the use case API
    // ontop of the contexts API as the context API doesn't return the use case category
    (acc, useCase) => {
      const category = useCaseMapping[useCase.name]?.category;
      if (!acc[category]) acc[category] = [];
      acc[category].push(useCase);
      return acc;
    },
    {} as Record<string, { id: string; name: string }[]>,
  );

  /* Render */
  return (
    <CustomDropdown
      triggerBtn={
        <Button>
          Use&nbsp;case
          {selectedIds.length > 0 && selectedIds.length !== useCases.length && (
            <span className="indicator-item flex justify-center items-center rounded-full bg-primary size-5 text-white">{selectedIds.length}</span>
          )}
          <ChevronDownIcon className="size-4" />
        </Button>
      }
      triggerBtnClassname="p-0"
    >
      <div className="p-2">
        <label className="flex items-center gap-2">
          <Checkbox checked={selectedIds.length === useCases.length} size="sm" onChange={() => handleCheckboxChange("all")} label="All" />
        </label>
        {Object.entries(categorizedUseCases).map(([category, cases]) => (
          <div key={category} className="mt-2">
            <h4 className="font-semibold text-sm mb-1">{category}</h4>
            {cases.map((useCase) => (
              <Checkbox key={useCase.id} checked={selectedIds.includes(useCase.id)} size="sm" onChange={() => handleCheckboxChange(useCase.id)} label={useCase.name} />
            ))}
          </div>
        ))}
      </div>
    </CustomDropdown>
  );
};
